import { createContext, useEffect, useReducer } from 'react'
import PropTypes from 'prop-types'

import axios from '../../axios'
import { isValidToken, setSession, setPhoneNumber } from '../../utils/jwt'

const initialState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
  message: '',
  hasError: false,
}

const handlers = {
  INITIALIZE: (state, action) => {
    const { isAuthenticated, user } = action.payload
    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user,
    }
  },
  MAKE_OTP_CODE: (state, action) => {
    const { message } = action.payload

    return {
      ...state,
      message,
    }
  },
  LOGIN: (state, action) => {
    const { user } = action.payload

    return {
      ...state,
      isAuthenticated: true,
      user,
    }
  },
  LOGIN_ERROR: (state, action) => {
    const { message, hasError } = action.payload

    return {
      ...state,
      message,
      hasError,
    }
  },
  LOGOUT: (state) => ({
    ...state,
    isAuthenticated: false,
    user: null,
  }),
}

const reducer = (state, action) =>
  handlers[action.type] ? handlers[action.type](state, action) : state

const AuthContext = createContext({
  ...initialState,
  method: 'jwt',
  login: () => Promise.resolve(),
  verifyCode: () => Promise.resolve(),
  logout: () => Promise.resolve(),
})

AuthProvider.propTypes = {
  children: PropTypes.node,
}

function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState)

  useEffect(() => {
    initialize()
  }, [])

  const initialize = async () => {
    try {
      const accessToken = window.localStorage.getItem('adminAccessToken')

      if (accessToken && isValidToken(accessToken)) {
        setSession(accessToken)

        const response = await axios.get('/v1/admin/profile')
        const { data } = response.data

        dispatch({
          type: 'INITIALIZE',
          payload: {
            isAuthenticated: true,
            user: data,
          },
        })
      } else {
        dispatch({
          type: 'INITIALIZE',
          payload: {
            isAuthenticated: false,
            user: null,
          },
        })
      }
    } catch (err) {
      console.error(err)
      dispatch({
        type: 'INITIALIZE',
        payload: {
          isAuthenticated: false,
          user: null,
        },
      })
    }
  }

  const login = async (phoneNumber) => {
    const response = await axios.post('/v1/auth/make-otp-code', {
      phoneNumber,
      role: 'admin',
    })

    setPhoneNumber(phoneNumber)
    const { message } = response.data

    dispatch({
      type: 'MAKE_OTP_CODE',
      payload: {
        message,
      },
    })
  }

  const verifyCode = async (phoneNumber, code) => {
    try {
      const response = await axios.post('/v1/auth/login-with-otp', {
        phoneNumber,
        code,
        role: 'admin',
      })

      const { token } = response.data

      setSession(token)

      const userResponse = await axios.get('/v1/admin/profile')
      const { data } = userResponse.data

      dispatch({
        type: 'LOGIN',
        payload: {
          user: data,
        },
      })
    } catch (error) {
      const errorMessage =
        error.response && error.response.data.error
          ? error.response.data.error
          : error.message

      return { errorMessage }

      // dispatch({
      //   type: 'LOGIN_ERROR',
      //   payload: {
      //     message:
      //       error.response && error.response.data.error
      //         ? error.response.data.error
      //         : error.message,
      //     hasError: true,
      //   },
      // })
    }
  }

  const logout = async () => {
    setSession(null)

    dispatch({ type: 'LOGOUT' })
  }

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: 'jwt',
        login,
        verifyCode,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export { AuthContext, AuthProvider }
